<template>
  <div class="h-full flex flex-col overflow-hidden">

    <div v-if="!commits.length" class="flex-1 flex items-center justify-center text-gray-600 text-xs">
      No commits to display
    </div>

    <div v-else class="flex-1 overflow-y-auto select-none">
      <div class="relative">

        <!-- Vertical spine connecting all commit nodes -->
        <div class="absolute left-[30px] top-3 bottom-3 w-px bg-gray-700" />

        <div
          v-for="(entry, i) in commits"
          :key="entry.hash"
          class="relative flex items-start gap-3 px-3 py-1.5 hover:bg-gray-800/50 cursor-pointer"
          @click="$emit('select', entry)"
        >
          <!-- Commit node on the spine -->
          <div class="relative z-10 flex items-center justify-center w-5 h-5 shrink-0 mt-0.5">
            <div
              class="rounded-full border-2 transition-colors"
              :class="i === 0
                ? 'w-3 h-3 bg-blue-500 border-blue-400'
                : 'w-2.5 h-2.5 bg-gray-900 border-gray-600 group-hover:border-gray-400'"
            />
          </div>

          <!-- Message + meta -->
          <div class="flex-1 min-w-0">
            <div class="text-xs text-gray-200 truncate leading-snug">{{ entry.message }}</div>
            <div class="flex items-center gap-1.5 mt-0.5">
              <code class="text-[10px] font-mono text-blue-400">{{ entry.hash.slice(0, 7) }}</code>
              <span class="text-[10px] text-gray-600 truncate max-w-24">{{ entry.author_name }}</span>
            </div>
          </div>

          <!-- Relative timestamp -->
          <time
            class="text-[10px] text-gray-600 shrink-0 mt-0.5 tabular-nums"
            :title="entry.date"
          >{{ relativeDate(entry.date) }}</time>
        </div>

      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import type { GitCommit } from '@/services/gitService'

defineProps<{ commits: GitCommit[] }>()
defineEmits<{ (e: 'select', commit: GitCommit): void }>()

function relativeDate(dateStr: string): string {
  try {
    const diff  = Date.now() - new Date(dateStr).getTime()
    const mins  = Math.floor(diff / 60_000)
    const hours = Math.floor(diff / 3_600_000)
    const days  = Math.floor(diff / 86_400_000)
    if (mins  < 1)   return 'just now'
    if (mins  < 60)  return `${mins}m ago`
    if (hours < 24)  return `${hours}h ago`
    if (days  < 7)   return `${days}d ago`
    return new Date(dateStr).toLocaleDateString('en', { month: 'short', day: 'numeric' })
  } catch {
    return dateStr
  }
}
</script>
